import { useState } from 'react';

import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import { ChevronDown, CircleAlert } from 'lucide-react';

import { OptionalTooltip } from '@modules/_shared/components/OptionalTooltip';
import { useLocale } from '@shared/hooks/locale';

type BulkActionsMenuProps = {
  disabled: boolean;
  emailDisabled: boolean;
  emailTooltipTitle: string;
  emailTooltipDisabled: boolean;
  showEmailWarning: boolean;
  onSendEmail: () => void;
  onMoveToColumn?: () => void;
};

export const BulkActionsMenu: React.FC<BulkActionsMenuProps> = ({
  disabled,
  emailDisabled,
  emailTooltipTitle,
  emailTooltipDisabled,
  showEmailWarning,
  onSendEmail,
  onMoveToColumn,
}) => {
  const { t } = useLocale();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const actionsOpened = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        id="toolbar-actions-button"
        variant="contained"
        disableElevation
        color="primary"
        onClick={e => setAnchorEl(e.currentTarget)}
        aria-controls={actionsOpened ? 'toolbar-actions-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={actionsOpened ? 'true' : undefined}
        endIcon={<ChevronDown />}
        disabled={disabled}
      >
        {t('Bulk actions')}
      </Button>
      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        id="toolbar-actions-menu"
        slotProps={{
          list: {
            'aria-labelledby': 'toolbar-actions-button',
          },
        }}
        anchorEl={anchorEl}
        open={actionsOpened}
        onClose={handleClose}
        sx={{ mt: 1 }}
      >
        <OptionalTooltip
          placement="top"
          title={emailTooltipTitle}
          disabled={emailTooltipDisabled}
        >
          <div>
            <MenuItem
              disabled={emailDisabled}
              onClick={() => {
                onSendEmail();
                handleClose();
              }}
            >
              <ListItemText>{t('Send email')}</ListItemText>
              {showEmailWarning && (
                <ListItemIcon sx={{ flexDirection: 'row-reverse' }}>
                  <CircleAlert size={20} color={theme.palette.warning.main} />
                </ListItemIcon>
              )}
            </MenuItem>
          </div>
        </OptionalTooltip>
        {onMoveToColumn != null && (
          <MenuItem
            onClick={() => {
              onMoveToColumn();
              handleClose();
            }}
          >
            {t('Move to column')}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
